import {
    alpha,
    Avatar,
    Box,
    Chip,
    Grid2,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { AuctionCatalogueStatusEnum } from '../../../_common/enum/auctionCatalogueStatus.enum';
import { StandardButton } from '../../../_components/button/primary.button';
import { StandardPanel } from '../../../_components/panel/standard.panel';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { useCatalogueBid } from '../../../_hubs/catalogueItemBid.hub';
import { catalogueItemUserBidService } from '../../../_services/catalogueItemBidService.service';
import { catalogueItemService } from '../../../_services/catalogueItemService.service';
import { OnlineStatusEnum } from '../../../_common/enum/onlineStatus.enum';
import { green, red } from '@mui/material/colors';
import { ActualAuctionStatusEnum } from '../../../_common/enum/actualAuctionStatus';
import { ConfirmDialog } from '../../../_components/notification/confirmDialog.component';
import { toastr } from '../../../_components/toastr/toastr';
import { accountService } from '../../../_services/accounts.service';
import { MessageNotification } from '../../../_components/box/message-notification';


export function AuctioncatalogueItemUserBids(params: {
    catalogueItem?: any,
    onlineUsers?: string[],
    actualAuctionStatusId: ActualAuctionStatusEnum
}) {

    const { bids, connected } = useCatalogueBid(params.catalogueItem?.id);

    const [data, setData] = useState<any[]>([]);
    const latestBidsRef = useRef<Array<any> | null>(null);

    const [loading, setLoading] = useState(true);
    const [selectedBidIds, setSelectedBidIds] = useState<Array<number>>([]);

    const [modalds, setModals] = useState({
        isWithdrawModalOpen: false,
        isUnderMinimumBid: false
    });

    const isLargesBid = (bid: number) => !!bid && !data.some((m: any) => m.bid?.bid > bid);
    const getBidColor = (bid: number) => (!!bid && params.catalogueItem?.minBid > bid) ? "info" : (!!bid && params.catalogueItem?.maxBid < bid) ? "error" : (!!bid && params.catalogueItem?.minBid <= bid) ? "success" : "default";
    const canSelectBids = accountService.isAdmin && params.actualAuctionStatusId === ActualAuctionStatusEnum.BidAcceptance;

    function mergeBidUpdates(users: any[], updates: Array<any> | null | undefined) {
        if (!Array.isArray(users) || users.length === 0) return users ?? [];
        if (!updates?.length) return users;

        const bidsByUserId = new Map<number, any>();
        updates.forEach((update) => {
            const userId = Number(update?.catalogueUserId);
            if (userId > 0) {
                bidsByUserId.set(userId, update);
            }
        });

        return users.map((user: any) => {
            const update = bidsByUserId.get(Number(user?.id));
            if (!update) return user;

            const currentBidId = user?.bid?.id;
            const currentBidAmount = user?.bid?.bid;
            const nextBidId = update?.id;
            const nextBidAmount = update?.bid;

            if (currentBidId === nextBidId && currentBidAmount === nextBidAmount) {
                return user;
            }

            return {
                ...user,
                bid: update,
            };
        });
    }

    useEffect(() => {
        latestBidsRef.current = bids ?? null;
        if (!bids?.length) return;

        setData((previous: any[]) => mergeBidUpdates(previous ?? [], bids));
    }, [bids]);

    useEffect(() => {
        if (!!data && !!data.length) {
            setOnlineStatus();
        }
    }, [params.onlineUsers]);

    useEffect(() => {
        // Fully reset results when switching catalogue items so stale bids from
        // the previous item don't linger while the new item's bids load.
        setData([]);
        setSelectedBidIds([]);
        latestBidsRef.current = null;

        if (!!params.catalogueItem?.id) {
            fetchData(params.catalogueItem?.id);
        }
        else {
            setLoading(false)
        }
    }, [params.catalogueItem?.id]);

    useEffect(() => {
        if (!!params.catalogueItem?.auctionCatalogueStatusId && !!data?.length) {
            if (params.catalogueItem?.auctionCatalogueStatusId === AuctionCatalogueStatusEnum.BidAcceptance) {
                setSelectedBidIds(data.filter((_d: any) => _d.bid?.bid && isLargesBid(_d.bid?.bid)).map((_d: any) => _d.bid.id));
            }
        }
    }, [params.catalogueItem?.auctionCatalogueStatusId, data]);

    function setOnlineStatus() {
        setData((_d: any) => {
            let __d = [..._d];
            __d.forEach((___d) => {
                ___d.onlineStatusId = params.onlineUsers?.some(u => u == ___d.userId) ? OnlineStatusEnum.Online : OnlineStatusEnum.Offline;
            })

            return __d;
        });
    }

    function fetchData(id: number) {
        setLoading(true);
        return catalogueItemUserBidService.getActive(id)
            .then(
                data => {
                    if (!!params.onlineUsers && !!params.onlineUsers.length) {
                        data.forEach((___d: any) => {
                            ___d.onlineStatusId = params.onlineUsers?.some(u => u == ___d.userId) ? OnlineStatusEnum.Online : OnlineStatusEnum.Offline;
                        })
                    }
                    setData(mergeBidUpdates(Array.isArray(data) ? data : [], latestBidsRef.current));
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function tryComplete() {
        if (!data || !data.length) {
            toastr.error("Invalid bid");
            return;
        }

        var winningBid = data.find((m: any) => m.bid.id == selectedBidIds[0]);
        if (winningBid.bid.bid < params.catalogueItem?.minBid) {
            setModals(m => ({ ...m, isUnderMinimumBid: true }))
        }
        else {
            complete();
        }
    }

    function complete() {

        setLoading(true);
        return catalogueItemService.complete(params.catalogueItem?.id, selectedBidIds[0])
            .then(
                data => {
                    setModals(m => ({ ...m, isUnderMinimumBid: false }))
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function reoffer() {
        setLoading(true);
        return catalogueItemService.reoffer(params.catalogueItem?.id, selectedBidIds)
            .then(
                data => {
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    function withdraw() {
        setLoading(true);
        return catalogueItemService.withdraw(params.catalogueItem?.id)
            .then(
                data => {
                    setModals(m => ({ ...m, isWithdrawModalOpen: false }))
                }, handleErrorResponse)
            .finally(() => {
                setLoading(false);
            });
    }

    return (
        <>
            <Grid2 container spacing={1}>
                <Grid2 size={{ xs: 12 }}>
                    <StandardPanel title={!!loading ? "Loading.." : !!params.catalogueItem?.id ? `Bidding Results #${params.catalogueItem?.id}` : undefined} loading={loading}>

                        {(!loading && !!data && !!data.length) &&
                            <List disablePadding>
                                {data.sort((a: any, b: any) => (b.bid?.bid ?? -Infinity) - (a.bid?.bid ?? -Infinity)).map((user: any) => {
                                    const rowContent = (
                                        <>
                                            <ListItemAvatar>
                                                <Avatar sx={{ bgcolor: user.onlineStatusId === OnlineStatusEnum.Online ? green[500] : red[500] }} src={user.userName} alt={user.userName} />
                                            </ListItemAvatar>

                                            <ListItemText
                                                primary={user.userName}
                                                secondary={user.clientName || "-"}
                                                sx={{ minWidth: 0 }}
                                                primaryTypographyProps={{ noWrap: true }}
                                                secondaryTypographyProps={{ noWrap: true }}
                                            />

                                            <Chip
                                                size="small"
                                                label={"€" + (user.bid?.bid || 0).toFixed(2)}
                                                variant="filled"
                                                color={getBidColor(user.bid?.bid)}
                                                sx={{ mx: 1, height: 22, marginRight: "25px", flexShrink: 0 }}
                                            />
                                        </>
                                    );

                                    return (
                                    <ListItem key={user.id}
                                        disablePadding
                                        sx={(theme) => ({
                                            backgroundColor: !!selectedBidIds && !!selectedBidIds.length && selectedBidIds.some(m => m == user.bid?.id) ? alpha(theme.palette.success.main, 0.16) : ""
                                        })}>

                                        {canSelectBids ?
                                        <ListItemButton
                                            disabled={!user?.bid?.id}
                                            onClick={() => {
                                                if (!!selectedBidIds && !!selectedBidIds.length && selectedBidIds.some(m => m == user.bid?.id)) {
                                                    setSelectedBidIds((prev) => prev.filter((id) => id !== user.bid?.id));
                                                }
                                                else {
                                                    setSelectedBidIds((_bids) => ([..._bids, user.bid?.id]));
                                                }
                                            }}>
                                            {rowContent}
                                        </ListItemButton>
                                        :
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 2, width: '100%' }}>
                                            {rowContent}
                                        </Box>
                                        }

                                    </ListItem>
                                    );
                                })}
                            </List>
                        }

                        {!loading && (!data || !data.length) &&
                            <MessageNotification message='No active catalogue item selected' />
                        }
                    </StandardPanel>
                </Grid2>

                {(!!accountService.isAdmin && (!!data && !!params.catalogueItem)) &&
                    <>
                        <Grid2 size={{ xs: 4 }}>
                            <StandardButton
                                icon="check"
                                variant="success"
                                disabled={!(!!selectedBidIds && selectedBidIds.length === 1) || params.actualAuctionStatusId !== ActualAuctionStatusEnum.BidAcceptance}
                                onClick={tryComplete}>
                                Approve Selected Bid
                            </StandardButton>
                        </Grid2>


                        <Grid2 size={{ xs: 4 }}>
                            <StandardButton
                                icon="restart_alt"
                                variant="info"
                                disabled={(!selectedBidIds || selectedBidIds.length < 2) || params.actualAuctionStatusId !== ActualAuctionStatusEnum.BidAcceptance}
                                //isabled
                                onClick={reoffer}>
                                Request New Bid
                            </StandardButton>
                        </Grid2>


                        <Grid2 size={{ xs: 4 }}>
                            <StandardButton
                                icon="clear"
                                variant="error"
                                disabled={!(!selectedBidIds || selectedBidIds.length === 0) || params.actualAuctionStatusId !== ActualAuctionStatusEnum.BidAcceptance}
                                onClick={() => {
                                    setModals(m => ({ ...m, isWithdrawModalOpen: true }))
                                }}>
                                Withdraw Item
                            </StandardButton>
                        </Grid2>
                    </>
                }
            </Grid2>

            <ConfirmDialog open={modalds.isWithdrawModalOpen} loading={loading} title='Are you sure you want to withdraw this catalogue item?'
                onClose={() => {
                    setModals(m => ({ ...m, isWithdrawModalOpen: false }))
                }}
                onConfirm={withdraw}
            />

            <ConfirmDialog open={modalds.isUnderMinimumBid} loading={loading} title='Are you sure you want to approve bid under minimum set price?'
                onClose={() => {
                    setModals(m => ({ ...m, isUnderMinimumBid: false }))
                }}
                onConfirm={complete}
            />

        </>
    )
}
